import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApiCall } from "../helper/axios";
const MODULE_KEY = "affiliate_account_performance";
// ==========================================================================
// INITIAL STATE
// ==========================================================================
const initialState = {
    lists: [],
    currentListDetails: [],
    currentTrackerInfo: null,
    semrushSuggestions: [],
    semrushOrganicData: [],
    clientPendingLists: [],
    loading: false,
    error: null,
    searchName: "",
    selectedClientId: "",
    selectedStatus: "",
    sortBy: "createdAt",
    sortOrder: "desc",
};
// 1. Fetch outreach tracker lists
const getApiData = (response) => response?.data ?? response?.response?.data;
const getApiErrorMessage = (response, err, fallback) => {
    const data = getApiData(response);
    return data?.message || err?.response?.data?.message || err?.message || fallback;
};
// 1. Fetch all tracker lists
export const fetchTrackerLists = createAsyncThunk("affiliateTracker/fetchTrackerLists", async (_, { getState, rejectWithValue }) => {
    try {
        const state = getState().affiliateTracker;
        const token = getState().auth?.token || "";
        let url = "/api/affiliate-tracker/list?";
        if (state.searchName)
            url += `name=${encodeURIComponent(state.searchName)}&`;
        if (state.selectedClientId)
            url += `client_id=${encodeURIComponent(state.selectedClientId)}&`;
        if (state.selectedStatus)
            url += `status=${encodeURIComponent(state.selectedStatus)}&`;
        if (state.sortBy)
            url += `sortBy=${encodeURIComponent(state.sortBy)}&`;
        if (state.sortOrder)
            url += `sortOrder=${encodeURIComponent(state.sortOrder)}&`;
        const response = await ApiCall("GET", url, undefined, {
            Authorization: token,
            "x-module-key": MODULE_KEY
        });
        const data = getApiData(response);
        if (data?.success)
            return data.data || [];
        return rejectWithValue(getApiErrorMessage(response, null, "Failed to fetch tracker lists"));
    }
    catch (err) {
        return rejectWithValue(getApiErrorMessage(null, err, "An error occurred"));
    }
});
// 2. Fetch specific tracker list details (contacts flat rows)
export const fetchTrackerDetails = createAsyncThunk("affiliateTracker/fetchTrackerDetails", async ({ id: trackerId, noContact }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const url = `/api/affiliate-tracker/details?id=${trackerId}${noContact ? '&no_contact=true' : ''}`;
        const response = await ApiCall("GET", url, undefined, {
            Authorization: token,
            "x-module-key": MODULE_KEY
        });
        const resData = getApiData(response);
        if (resData?.success) {
            const rawData = resData.data;
            const contacts = Array.isArray(rawData) ? rawData : (rawData?.contacts || []);
            const tracker = resData.tracker || rawData?.tracker || null;
            return { contacts, tracker };
        }
        return rejectWithValue(getApiErrorMessage(response, null, "Failed to fetch tracker details"));
    }
    catch (err) {
        return rejectWithValue(getApiErrorMessage(null, err, "An error occurred"));
    }
});
// 3. Manage tracker (create / update / delete)
export const manageTrackerList = createAsyncThunk("affiliateTracker/manageTrackerList", async ({ action, payload }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("POST", "/api/affiliate-tracker/manage", { action, ...payload }, {
            Authorization: token,
            "x-module-key": MODULE_KEY
        });
        const data = getApiData(response);
        if (data?.success)
            return data.data;
        return rejectWithValue(getApiErrorMessage(response, null, `Failed to ${action} tracker list`));
    }
    catch (err) {
        return rejectWithValue(getApiErrorMessage(null, err, "An error occurred"));
    }
});
// 4. Search Semrush keywords
export const searchSemrushKeywords = createAsyncThunk("affiliateTracker/searchSemrush", async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("POST", "/api/affiliate-tracker/semrush/search", payload, {
            Authorization: token,
            "x-module-key": MODULE_KEY
        });
        const data = getApiData(response);
        if (data?.success)
            return data.data;
        return rejectWithValue(getApiErrorMessage(response, null, "Failed to search SEMrush keywords"));
    }
    catch (err) {
        return rejectWithValue(getApiErrorMessage(null, err, "An error occurred"));
    }
});
// 5. Ingest / Import tracker Excel sheet
export const importTrackerExcel = createAsyncThunk("affiliateTracker/importExcel", async (formData, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("POST", "/api/affiliate-tracker/import-excel", formData, {
            Authorization: token,
            "x-module-key": MODULE_KEY,
            "Content-Type": "multipart/form-data"
        });
        const data = getApiData(response);
        if (data?.success)
            return data.data;
        return rejectWithValue(getApiErrorMessage(response, null, "Failed to import Excel spreadsheet"));
    }
    catch (err) {
        return rejectWithValue(getApiErrorMessage(null, err, "An error occurred"));
    }
});
// 6. Send list for client approval workflow
export const sendTrackerForApproval = createAsyncThunk("affiliateTracker/sendForApproval", async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("POST", "/api/affiliate-tracker/send-for-approval", payload, {
            Authorization: token,
            "x-module-key": MODULE_KEY
        });
        const data = getApiData(response);
        if (data?.success)
            return data.data;
        return rejectWithValue(getApiErrorMessage(response, null, "Failed to send list for approval"));
    }
    catch (err) {
        return rejectWithValue(getApiErrorMessage(null, err, "An error occurred"));
    }
});
// 7. Client respond (Approve / Reject outreach contact)
export const clientRespondTracker = createAsyncThunk("affiliateTracker/clientRespond", async (payload, { rejectWithValue }) => {
    try {
        const response = await ApiCall("POST", "/api/affiliate-tracker/client-respond", payload, undefined);
        const data = getApiData(response);
        if (data?.success)
            return data.data;
        return rejectWithValue(getApiErrorMessage(response, null, "Failed to submit response"));
    }
    catch (err) {
        return rejectWithValue(getApiErrorMessage(null, err, "An error occurred"));
    }
});
// 8. Fetch client tracker negative keywords (saved excludes)
export const fetchClientNegativeKeywords = createAsyncThunk("affiliateTracker/fetchClientNegativeKeywords", async (clientId, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("GET", `/api/affiliate-tracker/client-negative-keywords?client_id=${clientId}`, undefined, {
            Authorization: token,
            "x-module-key": MODULE_KEY
        });
        const data = getApiData(response);
        if (data?.success)
            return data.data ?? { domain: [], keyword: [] };
        return rejectWithValue(getApiErrorMessage(response, null, "Failed to fetch client negative keywords"));
    }
    catch (err) {
        return rejectWithValue(getApiErrorMessage(null, err, "An error occurred"));
    }
});
// 9. Save client tracker negative keywords
export const saveClientNegativeKeywords = createAsyncThunk("affiliateTracker/saveClientNegativeKeywords", async ({ clientId, keywords, domains }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("POST", "/api/affiliate-tracker/client-negative-keywords", { client_id: clientId, keywords, domains }, {
            Authorization: token,
            "x-module-key": MODULE_KEY
        });
        const data = getApiData(response);
        if (data?.success)
            return data.data ?? { domain: [], keyword: [] };
        return rejectWithValue(getApiErrorMessage(response, null, "Failed to save client negative keywords"));
    }
    catch (err) {
        return rejectWithValue(getApiErrorMessage(null, err, "An error occurred"));
    }
});
// 10. Fetch Client pending outreach approval lists
export const fetchClientPendingLists = createAsyncThunk("affiliateTracker/fetchClientPendingLists", async (clientId, { rejectWithValue }) => {
    try {
        const response = await ApiCall("GET", `/api/affiliate-tracker/client/pending-lists?client_id=${clientId}`, undefined, undefined);
        const data = getApiData(response);
        if (data?.success)
            return data.data || [];
        return rejectWithValue(getApiErrorMessage(response, null, "Failed to fetch client pending lists"));
    }
    catch (err) {
        return rejectWithValue(getApiErrorMessage(null, err, "An error occurred"));
    }
});
// ==========================================================================
// SLICE
// ==========================================================================
const affiliateTrackerSlice = createSlice({
    name: "affiliateTracker",
    initialState,
    reducers: {
        setSearchFilters: (state, action) => {
            return { ...state, ...action.payload };
        },
        clearSemrushData: (state) => {
            state.semrushSuggestions = [];
            state.semrushOrganicData = [];
        },
        clearCurrentListDetails: (state) => {
            state.currentListDetails = [];
            state.currentTrackerInfo = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // fetchTrackerLists
            .addCase(fetchTrackerLists.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchTrackerLists.fulfilled, (state, action) => {
            state.loading = false;
            state.lists = action.payload || [];
        })
            .addCase(fetchTrackerLists.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || "Failed to fetch tracker lists";
        })
            // fetchTrackerDetails
            .addCase(fetchTrackerDetails.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchTrackerDetails.fulfilled, (state, action) => {
            state.loading = false;
            state.currentListDetails = action.payload?.contacts || [];
            state.currentTrackerInfo = action.payload?.tracker || null;
        })
            .addCase(fetchTrackerDetails.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || "Failed to fetch tracker list details";
        })
            // searchSemrushKeywords
            .addCase(searchSemrushKeywords.pending, (state) => {
            state.error = null;
        })
            .addCase(searchSemrushKeywords.fulfilled, (state, action) => {
            state.semrushSuggestions = action.payload?.keyword_idea || [];
            state.semrushOrganicData = action.payload?.keyword_data || [];
        })
            .addCase(searchSemrushKeywords.rejected, (state, action) => {
            state.error = action.payload || "Failed to query SEMrush data";
        })
            // fetchClientPendingLists
            .addCase(fetchClientPendingLists.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchClientPendingLists.fulfilled, (state, action) => {
            state.loading = false;
            state.clientPendingLists = action.payload || [];
        })
            .addCase(fetchClientPendingLists.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || "Failed to retrieve pending client lists";
        });
    }
});
export const { setSearchFilters, clearSemrushData, clearCurrentListDetails } = affiliateTrackerSlice.actions;
export default affiliateTrackerSlice.reducer;
